import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { createLogger } from '../../../utils/debugUtils';
import { useAuth } from '../../../context/AuthContext';
import CreditEstimator from '../../usage/CreditEstimator';

const logger = createLogger('Step3_ResearchConfig');

export default function Step3_ResearchConfig({
  wizardState,
  onUpdateState,
  onNext,
  onBack,
  loading,
  setLoading,
  onSuccess
}) {
  const [error, setError] = useState(null);
  const [selectedChip, setSelectedChip] = useState(null);
  const [userHasEditedText, setUserHasEditedText] = useState(false);
  const { userProfile } = useAuth();

  // Check if user has access to web search feature
  const hasWebSearchAccess = userProfile?.enable_web_search_feature || false;

  // Use case chip definitions
  const useCaseChips = [
    {
      id: 'funded',
      label: 'Funded companies (Series A+)',
      icon: '💰',
      enableWebSearch: true,
      instructions: 'Company has raised Series A, Series B, or later stage funding in the last 24 months. Include companies that have received institutional venture capital backing from recognized VC firms. Exclude angel-only or seed-only funding.'
    },
    {
      id: 'b2b-saas',
      label: 'B2B SaaS companies',
      icon: '💻',
      enableWebSearch: false,
      instructions: 'Company is a B2B software-as-a-service business. They sell cloud-based software products to other businesses on a subscription basis. Exclude agencies, consulting firms, freelancers, and companies that only sell services or one-time software licenses.'
    },
    {
      id: 'industry',
      label: 'Specific industry focus',
      icon: '🎯',
      enableWebSearch: false,
      instructions: 'Company specifically targets the [INDUSTRY] industry. Look for case studies, client logos, or explicit messaging about serving [INDUSTRY] businesses. They should have dedicated solutions or expertise in this vertical.',
      hasPlaceholder: true,
      placeholder: '[INDUSTRY]'
    },
    {
      id: 'company-size',
      label: 'Company size range',
      icon: '👥',
      enableWebSearch: true,
      instructions: 'Company has between [MIN] and [MAX] employees based on LinkedIn company page data. This indicates they are past early startup stage but not yet enterprise-scale.',
      hasPlaceholder: true,
      placeholder: '[MIN] and [MAX]'
    },
    {
      id: 'social-media',
      label: 'Active on social media',
      icon: '📱',
      enableWebSearch: true,
      instructions: 'Company maintains active social media presence on LinkedIn, Twitter, or Instagram. Look for verified accounts with recent posts (within last 30 days) and meaningful follower engagement.'
    },
    {
      id: 'hiring',
      label: 'Hiring for specific roles',
      icon: '📢',
      enableWebSearch: true,
      instructions: 'Company is currently hiring for [ROLE TYPE] positions. Check LinkedIn jobs, company careers page, and job boards for active open positions. This indicates growth and investment in this function.',
      hasPlaceholder: true,
      placeholder: '[ROLE TYPE]'
    },
    {
      id: 'growing',
      label: 'Growing/scaling companies',
      icon: '📈',
      enableWebSearch: true,
      instructions: 'Company shows recent growth signals: new funding announcements, office expansions, leadership hires, product launches, or press coverage in the last 6 months. They should demonstrate momentum and scaling activity.'
    }
  ];

  // Handle chip selection - additive behavior
  const handleChipClick = (chipData) => {
    if (chipData.id === 'scratch') {
      // Start from scratch - clear everything
      setSelectedChip(null);
      setUserHasEditedText(false);
      onUpdateState({
        researchInstructions: '',
        enableWebSearch: false
      });
      logger.debug('Start from scratch selected');
    } else {
      // Add chip instructions to existing text
      const currentText = wizardState.researchInstructions || '';
      const separator = currentText.trim() ? '\n\n' : ''; // Add line breaks between criteria
      const newText = currentText + separator + chipData.instructions;

      // Toggle chip selection for visual feedback
      setSelectedChip(chipData.id);
      setUserHasEditedText(false);

      // Update instructions (additive)
      onUpdateState({
        researchInstructions: newText,
        // Enable web search if ANY chip requiring it is added
        enableWebSearch: wizardState.enableWebSearch || chipData.enableWebSearch
      });

      logger.debug('Chip added to criteria', {
        chipId: chipData.id,
        enableWebSearch: chipData.enableWebSearch,
        textLength: newText.length
      });

      // Clear chip selection after a brief moment (visual feedback)
      setTimeout(() => setSelectedChip(null), 500);
    }
  };

  // Handle text field changes with smart chip deselection
  const handleInstructionsChange = (e) => {
    const value = e.target.value;
    onUpdateState({ researchInstructions: value });
    logger.debug('Instructions updated', { length: value.length });

    // Deselect chip if user significantly edits the text
    if (selectedChip && selectedChip !== 'scratch') {
      const selectedChipData = useCaseChips.find(c => c.id === selectedChip);
      if (selectedChipData && value !== selectedChipData.instructions) {
        setUserHasEditedText(true);
        // Only deselect if edit is substantial (>20 chars difference)
        const originalLength = selectedChipData.instructions.length;
        if (Math.abs(value.length - originalLength) > 20) {
          setSelectedChip(null);
          logger.debug('Chip deselected due to substantial edits');
        }
      }
    }
  };

  // Note: Job creation now happens in ResearchAgentNewProject.js
  // This component only handles the UI and state updates

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 50/50 Split Layout - Match Step 2 */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Panel: Instructions */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{
            p: 3,
            backgroundColor: '#161c3e',
            borderRadius: 2,
            height: '580px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6">
                Qualification Criteria
              </Typography>
              {wizardState.appliedTemplateName && (
                <Chip
                  label={`Template: ${wizardState.appliedTemplateName}`}
                  size="small"
                  sx={{ bgcolor: '#faa32c', color: 'white' }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', whiteSpace: 'normal', wordWrap: 'break-word' }}>
              Describe what you want Wisp to look for on each website. You can use natural language and combine several criteria in your instructions. Include details that would qualify and/or disqualify a company for this campaign.
            </Typography>

            {/* Use Case Chips Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  color: 'text.primary'
                }}
              >
                What do you want to verify?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  fontSize: '0.875rem'
                }}
              >
                Select a common use case to get started, then customize as needed
              </Typography>

              {/* Chip Grid */}
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2
              }}>
                {useCaseChips.map((chip) => (
                  <Chip
                    key={chip.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{chip.icon}</span>
                        <span>{chip.label}</span>
                      </Box>
                    }
                    onClick={() => handleChipClick(chip)}
                    sx={{
                      backgroundColor: selectedChip === chip.id
                        ? '#faa32c'
                        : 'rgba(255, 255, 255, 0.05)',
                      color: selectedChip === chip.id ? '#000' : 'text.primary',
                      border: selectedChip === chip.id
                        ? '2px solid #faa32c'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      fontWeight: selectedChip === chip.id ? 600 : 400,
                      '&:hover': {
                        backgroundColor: selectedChip === chip.id
                          ? '#faa32c'
                          : 'rgba(255, 255, 255, 0.08)',
                        borderColor: selectedChip === chip.id
                          ? '#faa32c'
                          : 'rgba(255, 255, 255, 0.4)',
                      },
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  />
                ))}

                {/* Start from Scratch Chip */}
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>+</span>
                      <span>Start from scratch</span>
                    </Box>
                  }
                  onClick={() => handleChipClick({ id: 'scratch' })}
                  variant="outlined"
                  sx={{
                    backgroundColor: selectedChip === 'scratch'
                      ? 'rgba(0, 149, 255, 0.2)'
                      : 'transparent',
                    color: 'text.primary',
                    borderColor: selectedChip === 'scratch'
                      ? '#0095FF'
                      : 'rgba(255, 255, 255, 0.3)',
                    borderStyle: selectedChip === 'scratch' ? 'solid' : 'dashed',
                    fontWeight: selectedChip === 'scratch' ? 600 : 400,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 149, 255, 0.1)',
                      borderColor: '#0095FF',
                      borderStyle: 'solid'
                    },
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                />
              </Box>
            </Box>

            <TextField
              label="What should we look for?"
              multiline
              rows={6}
              fullWidth
              placeholder="Example: Verify the company is B2B and not B2C. I want retail businesses that have actual physical locations, not e-commerce stores."
              value={wizardState.researchInstructions || ''}
              onChange={handleInstructionsChange}
              sx={{ mb: 2 }}
            />

            {/* Enhanced Web Search - Only show if user has access */}
            {hasWebSearchAccess && (
              <Box sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: 'rgba(0, 149, 255, 0.05)',
                border: '1px solid rgba(0, 149, 255, 0.2)',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                    margin: 0,
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                      paddingTop: '9px'
                    }
                  }}
                  control={
                    <Checkbox
                      checked={wizardState.enableWebSearch || false}
                      onChange={(e) => {
                        onUpdateState({ enableWebSearch: e.target.checked });
                        logger.debug('Web search toggled', { enabled: e.target.checked });
                      }}
                      sx={{
                        color: '#0095FF',
                        '&.Mui-checked': {
                          color: '#0095FF',
                        },
                        padding: '9px'
                      }}
                    />
                  }
                  label={
                    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                        Enhanced Web Search
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                        Supplement website analysis with additional web search data (5 credits per website)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right Panel: Tips */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{
            p: 3,
            backgroundColor: '#161c3e',
            borderRadius: 2,
            height: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Credit Estimator */}
            <Box sx={{ mb: 3 }}>
              <CreditEstimator
                websiteCount={wizardState.rowCount || 0}
                creditsPerWebsite={wizardState.enableWebSearch ? 5 : 1}
                enhancedWebSearch={wizardState.enableWebSearch}
                showTierLimits={true}
                autoCheck={true}
              />
            </Box>

            {/* Tips Box */}
            <Box sx={{
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 1,
              p: 2,
              backgroundColor: 'rgba(0, 149, 255, 0.05)',
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              <Typography variant="subtitle2" gutterBottom sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                💡 Tips for better results:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, maxWidth: '100%', minWidth: 0 }}>
                <Typography component="li" variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                  Think about what a website must have in order to qualify
                </Typography>
                <Typography component="li" variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                  Think about what signals indicate a poor match
                </Typography>
                <Typography component="li" variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                  Examples: "I want to verify they are a SaaS company targeting the healthcare industry" or "I want companies offering physical products, not just services"
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
